import { useState } from 'react';
import { Alert, Divider, Drawer, MenuItem, Stack, TextField } from '@mui/material';

import { useSite } from '@/hooks/use-site';
import { useDebouncedBlockContent } from '@/hooks/use-debounced-block-content';
import { SITE_THEME_PRESETS } from '@/lib/site-styles/theme-presets';
import { DEFAULT_ACCESSIBILITY_PREFERENCES } from '@/lib/accessibility/types';
import type { AccessibilityPreferences } from '@/lib/accessibility/types';
import type { Site, SiteThemeConfig } from '@/types/database';
import { ScreenHeader } from './ScreenHeader';
import { ThemesSection } from './ThemesSection';
import { LogoSection } from './LogoSection';
import { FontsScreen } from './FontsScreen';
import { ColorsScreen } from './ColorsScreen';
import { ButtonsScreen } from './ButtonsScreen';
import { FormsScreen } from './FormsScreen';
import { SurfacesScreen } from './SurfacesScreen';
import { AccessibilityPreviewProvider } from './accessibility-preview-context';
import type { SiteStylesDraft } from './types';

// Editor-only preview of how each accessibility preference would render on
// top of the draft theme — never persisted, never sent to theme_config.
// Each state isolates one preference so it's clear what's being previewed,
// rather than stacking every preference at once.
const ACCESSIBILITY_PREVIEW_MODES: { value: string; label: string; preferences: AccessibilityPreferences }[] = [
  { value: 'default', label: 'Default', preferences: DEFAULT_ACCESSIBILITY_PREFERENCES },
  { value: 'large-text', label: 'Large text', preferences: { ...DEFAULT_ACCESSIBILITY_PREFERENCES, textSize: 'large' } },
  { value: 'increased-spacing', label: 'Increased spacing', preferences: { ...DEFAULT_ACCESSIBILITY_PREFERENCES, textSpacing: 'increased', contentSpacing: 'comfortable' } },
  { value: 'high-contrast-light', label: 'High-contrast light', preferences: { ...DEFAULT_ACCESSIBILITY_PREFERENCES, contrast: 'high-contrast-light' } },
  { value: 'high-contrast-dark', label: 'High-contrast dark', preferences: { ...DEFAULT_ACCESSIBILITY_PREFERENCES, contrast: 'high-contrast-dark' } },
  { value: 'reduced-motion', label: 'Reduced motion', preferences: { ...DEFAULT_ACCESSIBILITY_PREFERENCES, reduceMotion: true } },
  { value: 'keyboard-focus', label: 'Keyboard focus', preferences: { ...DEFAULT_ACCESSIBILITY_PREFERENCES, enhancedFocus: true } },
];

interface SiteStylesDrawerProps {
  open: boolean;
  onClose: () => void;
  site: Site;
  unitName: string;
}

// A single continuous scroll — Themes, Logo, Colors, Fonts, Buttons,
// Forms, Surfaces, all visible at once — matching the structure of
// Scouting Ops Hub's Website > Style tab (see
// scoutingops-hub/src/components/app/website-settings/style-section.tsx)
// instead of the drill-down, one-category-at-a-time screens this used to
// be. Still a drawer, not a modal — a working surface you flip between
// while looking at the canvas underneath, not a one-shot form.
export function SiteStylesDrawer({ open, onClose, site, unitName }: SiteStylesDrawerProps) {
  const { save, error } = useSite();
  const [previewMode, setPreviewMode] = useState('default');

  const [draft, update] = useDebouncedBlockContent<SiteStylesDraft>(
    { theme_config: site.theme_config },
    (next) => void save({ theme_config: next.theme_config })
  );

  const updateTheme = (theme_config: SiteThemeConfig, options?: { immediate?: boolean }) =>
    update({ theme_config }, options);

  const handleApplyPreset = (presetId: string) => {
    const preset = SITE_THEME_PRESETS.find((candidate) => candidate.id === presetId);
    if (preset) updateTheme(preset.config, { immediate: true });
  };

  const previewPreferences =
    ACCESSIBILITY_PREVIEW_MODES.find((mode) => mode.value === previewMode)?.preferences ?? DEFAULT_ACCESSIBILITY_PREFERENCES;

  return (
    <Drawer anchor="right" open={open} onClose={onClose} slotProps={{ paper: { sx: { width: { xs: '100%', sm: 880 } } } }}>
      <ScreenHeader title="Site Styles" onClose={onClose} />

      {error ? <Alert severity="error" sx={{ m: 2 }}>{error}</Alert> : null}

      <Stack sx={{ px: 2.5, pt: 2 }}>
        <TextField
          select
          label="Preview accessibility state"
          size="small"
          value={previewMode}
          onChange={(event) => setPreviewMode(event.target.value)}
          helperText="Previews only — doesn't change your saved site design."
          sx={{ maxWidth: 280 }}
        >
          {ACCESSIBILITY_PREVIEW_MODES.map((mode) => (
            <MenuItem key={mode.value} value={mode.value}>
              {mode.label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <AccessibilityPreviewProvider preferences={previewPreferences}>
        <Stack divider={<Divider />} sx={{ overflowY: 'auto' }}>
          <Stack sx={{ p: 2.5 }}>
            <ThemesSection themeConfig={draft.theme_config} onApplyPreset={handleApplyPreset} />
          </Stack>
          <Stack sx={{ p: 2.5 }}>
            <LogoSection logoUrl={site.logo_url} unitName={unitName} />
          </Stack>
          <ColorsScreen draft={draft} updateTheme={updateTheme} unitName={unitName} />
          <FontsScreen draft={draft} updateTheme={updateTheme} unitName={unitName} />
          <ButtonsScreen draft={draft} updateTheme={updateTheme} unitName={unitName} />
          <FormsScreen draft={draft} updateTheme={updateTheme} unitName={unitName} />
          <SurfacesScreen draft={draft} updateTheme={updateTheme} unitName={unitName} />
        </Stack>
      </AccessibilityPreviewProvider>
    </Drawer>
  );
}
